/**
 * SharePointTab — top-level project tab for SharePoint integration.
 *
 * Orchestrates the 4-step setup wizard:
 *   1. Tenant credentials status (read-only)
 *   2. Library URL configuration form
 *   3. File browser + import selection
 *   4. Live auto-sync card
 *
 * Each step lives in its own file under `components/sharepoint/`. This
 * component just loads the project's existing SharePoint config + company
 * credentials, then composes the steps.
 */
import { useCallback, useEffect, useState } from "react";
import { useI18n } from "../../context/I18nContext";
import { useAuth } from "../../context/AuthContext";
import { getSharePointConfig } from "../../lib/api";
import LiveSyncCard from "../LiveSyncCard";
import SharePointCredentialsStep from "./SharePointCredentialsStep";
import SharePointLibraryForm from "./SharePointLibraryForm";
import SharePointFileBrowser from "./SharePointFileBrowser";

export default function SharePointTab({ projectId, onImported }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;

  const [cfg, setCfg] = useState(null);
  const [companyCreds, setCompanyCreds] = useState({ configured: false });
  const [form, setForm] = useState({ library_url: "", include_subfolders: false });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getSharePointConfig(projectId);
      setCompanyCreds(d.company_credentials || { configured: false });
      if (d.configured && d.config) {
        setCfg(d.config);
        setForm({
          library_url: d.config.library_url || "",
          include_subfolders: !!d.config.include_subfolders,
        });
      }
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="text-sm text-zinc-500" data-testid="sharepoint-loading">{t("loading")}</div>;
  }

  const showFileBrowserAndSync = cfg?.status === "configured" && companyCreds.configured;

  return (
    <div className="max-w-4xl space-y-6" data-testid="sharepoint-tab">
      <SharePointCredentialsStep companyCreds={companyCreds} />

      <SharePointLibraryForm
        projectId={projectId}
        isAdmin={isAdmin}
        cfg={cfg}
        setCfg={setCfg}
        companyCreds={companyCreds}
        form={form}
        setForm={setForm}
      />

      {showFileBrowserAndSync && (
        <SharePointFileBrowser
          projectId={projectId}
          isAdmin={isAdmin}
          form={form}
          onImported={onImported}
        />
      )}

      {showFileBrowserAndSync && (
        <LiveSyncCard
          projectId={projectId}
          isAdmin={isAdmin}
          libraryUrl={form.library_url}
          includeSubfolders={form.include_subfolders}
          initialEnabled={cfg?.auto_sync_enabled}
          initialIntervalMinutes={cfg?.auto_sync_interval_minutes}
          onChange={(patch) => setCfg((prev) => ({ ...(prev || {}), ...patch }))}
        />
      )}
    </div>
  );
}
